import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import pagesAPI from '../../api/pages';
import ComponentPicker from './ComponentPicker';
import SectionEditor from './SectionEditor';
import './pages.css';

const PageBuilder = () => {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const isNewPage = pageId === 'new';

  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [editingSection, setEditingSection] = useState(null); // null = show list, object = show editor
  const [showComponentPicker, setShowComponentPicker] = useState(false);
  const [loading, setLoading] = useState(!isNewPage);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('sections'); // 'sections' | 'seo' | 'details'

  // Page metadata
  const [pageName, setPageName] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [pageType, setPageType] = useState('landing');
  const [status, setStatus] = useState('draft');
  const [slug, setSlug] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  // SEO data
  const [seo, setSeo] = useState({
    focusKeyword: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    metaRobots: 'index,follow',
    ogTitle: '',
    ogDescription: '',
    ogImage: null,
    ogType: 'website',
    ogLocale: 'en_US',
    twitterCard: 'summary_large_image',
    twitterSite: '',
    twitterCreator: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: null,
    canonicalUrl: '',
    noindex: false,
    nofollow: false,
    schemaType: 'WebPage',
    structuredData: null,
  });

  useEffect(() => {
    if (!isNewPage) {
      fetchPage();
    }
  }, [pageId]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await pagesAPI.getPageById(pageId);

      if (response?.data?.status === 200 || response?.data?.success) {
        const pageData = response.data.data;
        setPage(pageData);
        setPageName(pageData.pageName || pageData.title || '');
        setPageUrl(pageData.pageUrl || pageData.route || '');
        setPageType(pageData.pageType || 'landing');
        setStatus(pageData.status || 'draft');
        setSlug(pageData.slug || '');
        setIsPrivate(pageData.isPrivate || false);
        setSections(pageData.sections || []);

        // Load SEO data
        if (pageData.seo) {
          setSeo({
            focusKeyword: pageData.seo.focusKeyword || '',
            metaTitle: pageData.seo.metaTitle || '',
            metaDescription: pageData.seo.metaDescription || '',
            metaKeywords: pageData.seo.metaKeywords || '',
            metaRobots: pageData.seo.metaRobots || 'index,follow',
            ogTitle: pageData.seo.ogTitle || '',
            ogDescription: pageData.seo.ogDescription || '',
            ogImage: pageData.seo.ogImage || null,
            ogType: pageData.seo.ogType || 'website',
            ogLocale: pageData.seo.ogLocale || 'en_US',
            twitterCard: pageData.seo.twitterCard || 'summary_large_image',
            twitterSite: pageData.seo.twitterSite || '',
            twitterCreator: pageData.seo.twitterCreator || '',
            twitterTitle: pageData.seo.twitterTitle || '',
            twitterDescription: pageData.seo.twitterDescription || '',
            twitterImage: pageData.seo.twitterImage || null,
            canonicalUrl: pageData.seo.canonicalUrl || '',
            noindex: pageData.seo.noindex || false,
            nofollow: pageData.seo.nofollow || false,
            schemaType: pageData.seo.schemaType || 'WebPage',
            structuredData: pageData.seo.structuredData || null,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      toast.error('Failed to load page');
      navigate('/pages');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = async (componentKey) => {
    if (isNewPage) {
      toast.warning('Please save the page first before adding sections');
      return;
    }

    try {
      const payload = {
        componentKey,
        order: sections.length,
      };

      const response = await pagesAPI.addSection(pageId, payload);

      if (response?.data?.status === 200 || response?.data?.success) {
        toast.success('Section added successfully');
        fetchPage();
        setShowComponentPicker(false);
      }
    } catch (error) {
      console.error('Error adding section:', error);
      toast.error('Failed to add section');
    }
  };

  const handleUpdateSection = async (sectionId, content, styles, sectionName) => {
    try {
      const payload = {
        content,
        styles,
        sectionName
      };
      const response = await pagesAPI.updateSection(pageId, sectionId, payload);

      if (response?.data?.status === 200 || response?.data?.success) {
        toast.success('Section updated successfully');
        setEditingSection(null); // Go back to list
        fetchPage();
      }
    } catch (error) {
      console.error('Error updating section:', error);
      toast.error('Failed to update section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section?')) return;

    try {
      const response = await pagesAPI.deleteSection(pageId, sectionId);

      if (response?.data?.status === 200 || response?.data?.success) {
        toast.success('Section deleted successfully');
        setEditingSection(null);
        fetchPage();
      }
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to delete section');
    }
  };

  const handleToggleSectionStatus = async (sectionId, currentStatus) => {
    try {
      const section = sections.find((s) => s.sectionId === sectionId);
      await pagesAPI.updateSection(pageId, sectionId, {
        content: section.content,
        styles: section.styles,
        status: !currentStatus,
      });
      toast.success('Section visibility updated');
      fetchPage();
    } catch (error) {
      console.error('Error updating section status:', error);
      toast.error('Failed to update section status');
    }
  };

  const moveSectionUp = async (index) => {
    if (index === 0) return;
    const newOrder = [...sections];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    await reorderSections(newOrder);
  };

  const moveSectionDown = async (index) => {
    if (index === sections.length - 1) return;
    const newOrder = [...sections];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    await reorderSections(newOrder);
  };

  const reorderSections = async (newOrder) => {
    try {
      const payload = {
        sections: newOrder.map((s, index) => ({
          sectionId: s.sectionId,
          order: index,
        })),
      };
      await pagesAPI.reorderSections(pageId, payload);
      toast.success('Sections reordered');
      fetchPage();
    } catch (error) {
      console.error('Error reordering sections:', error);
      toast.error('Failed to reorder sections');
    }
  };

  const handleSavePageDetails = async () => {
    if (!pageName.trim()) {
      toast.error('Please enter a page name');
      return;
    }

    try {
      const payload = {
        pageName,
        pageUrl,
        pageType,
        status,
        slug,
        isPrivate,
        seo,
      };

      const response = await pagesAPI.updatePage(pageId, payload);

      if (response?.data?.status === 200 || response?.data?.success) {
        toast.success('Page details updated successfully');
        fetchPage();
      }
    } catch (error) {
      console.error('Error saving page details:', error);
      toast.error('Failed to save page details');
    }
  };

  const handlePublish = async () => {
    if (isNewPage) {
      toast.warning('Please save the page first');
      return;
    }

    try {
      const response = await pagesAPI.publishPage(pageId);

      if (response?.data?.status === 200 || response?.data?.success) {
        toast.success('Page published successfully');
        fetchPage();
      }
    } catch (error) {
      console.error('Error publishing page:', error);
      toast.error('Failed to publish page');
    }
  };

  const handleUnpublish = async () => {
    if (isNewPage) {
      toast.warning('Please save the page first');
      return;
    }

    try {
      const response = await pagesAPI.unpublishPage(pageId);

      if (response?.data?.status === 200 || response?.data?.success) {
        toast.success('Page unpublished successfully');
        fetchPage();
      }
    } catch (error) {
      console.error('Error unpublishing page:', error);
      toast.error('Failed to unpublish page');
    }
  };

  const handleGeneratePreviewLink = async () => {
    if (isNewPage) {
      toast.warning('Please save the page first');
      return;
    }

    if (status === 'published') {
      toast.info('Published pages can be viewed directly at their URL');
      return;
    }

    try {
      setGeneratingPreview(true);
      const response = await pagesAPI.generatePreviewToken(pageId);

      if (response?.data?.status === 200) {
        const { previewUrl: url } = response.data.data;
        const userAppUrl = process.env.REACT_APP_USER_APP_URL || window.location.origin;
        const fullPreviewUrl = `${userAppUrl.replace(/\/$/, '')}${url}`;
        setPreviewUrl(fullPreviewUrl);

        // Copy to clipboard
        navigator.clipboard.writeText(fullPreviewUrl);
        toast.success('Preview link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error generating preview link:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to generate preview link';
      toast.error(errorMessage);
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleCopyPreviewLink = () => {
    if (previewUrl) {
      navigator.clipboard.writeText(previewUrl);
      toast.success('Preview link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading page...</p>
      </div>
    );
  }

  // Show section editor if editing
  if (editingSection) {
    return (
      <div className="page-builder">
        <div className="box_main">
          <div className="header_top d-flex justify-content-between align-items-center">
            <div>
              <h3>Edit Section: {editingSection.componentKey}</h3>
              <p className="text-muted mb-0">Page: {pageName}</p>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setEditingSection(null)}
            >
              <i className="fa fa-arrow-left me-2"></i>
              Back to Sections
            </button>
          </div>
          <div className="middle_main">
            <SectionEditor
              section={editingSection}
              onUpdate={(content, styles, sectionName) =>
                handleUpdateSection(editingSection.sectionId, content, styles, sectionName)
              }
              onDelete={() => handleDeleteSection(editingSection.sectionId)}
              onClose={() => setEditingSection(null)}
            />
          </div>
        </div>
      </div>
    );
  }

  // Show sections list
  return (
    <div className="page-builder">
      <div className="box_main">
        <div className="header_top">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h3>{pageName || 'Untitled Page'}</h3>
              <p className="text-muted mb-0">
                <code>{pageUrl}</code> • {pageType} • {status}
              </p>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/pages')}
              >
                <i className="fa fa-arrow-left me-2"></i>
                Back to Pages
              </button>

              {/* Preview Link for Unpublished Pages */}
              {!isNewPage && status !== 'published' && (
                <button
                  className="btn btn-outline-info"
                  onClick={handleGeneratePreviewLink}
                  disabled={generatingPreview}
                  title="Generate preview link for unpublished page"
                >
                  {generatingPreview ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-eye me-2"></i>
                      Preview Link
                    </>
                  )}
                </button>
              )}

              {/* Show preview URL if generated */}
              {previewUrl && status !== 'published' && (
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={previewUrl}
                    readOnly
                    style={{ width: '400px', fontSize: '0.85rem' }}
                  />
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleCopyPreviewLink}
                    title="Copy preview link"
                  >
                    <i className="fa fa-copy"></i>
                  </button>
                </div>
              )}

              {status === 'published' ? (
                <button
                  className="btn btn-warning"
                  onClick={handleUnpublish}
                  disabled={isNewPage}
                >
                  <i className="fa fa-eye-slash me-2"></i>
                  Unpublish
                </button>
              ) : (
                <button
                  className="btn btn-success"
                  onClick={handlePublish}
                  disabled={isNewPage}
                >
                  <i className="fa fa-check me-2"></i>
                  Publish
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="middle_main">
          {/* Tab Navigation */}
          <ul className="nav nav-tabs mb-4" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'sections' ? 'active' : ''}`}
                onClick={() => setActiveTab('sections')}
                type="button"
              >
                <i className="fa fa-layer-group me-2"></i>
                Sections
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'seo' ? 'active' : ''}`}
                onClick={() => setActiveTab('seo')}
                type="button"
                disabled={isNewPage}
              >
                <i className="fa fa-search me-2"></i>
                SEO
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
                type="button"
              >
                <i className="fa fa-cog me-2"></i>
                Details
              </button>
            </li>
          </ul>

          {/* Sections Tab */}
          {activeTab === 'sections' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">
                  Sections ({sections.length})
                </h5>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowComponentPicker(true)}
                  disabled={isNewPage}
                >
                  <i className="fa fa-plus me-2"></i>
                  Add Section
                </button>
              </div>

          {sections.length === 0 ? (
            <div className="empty-state text-center py-5">
              <i className="fa fa-layer-group fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No sections added yet</h5>
              <p className="text-muted">Add your first section to start building this page</p>
              <button
                className="btn btn-primary mt-3"
                onClick={() => setShowComponentPicker(true)}
                disabled={isNewPage}
              >
                <i className="fa fa-plus me-2"></i>
                Add Section
              </button>
              {isNewPage && <p className="text-muted small mt-2">Save page first</p>}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>Order</th>
                    <th>Component</th>
                    <th style={{ width: '100px' }}>Status</th>
                    <th style={{ width: '200px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sections
                    .sort((a, b) => a.order - b.order)
                    .map((section, index) => (
                      <tr key={section.sectionId}>
                        <td>
                          <strong>#{section.order + 1}</strong>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="fa fa-grip-vertical text-muted me-3"></i>
                            <div>
                              <strong>{section.sectionName || section.componentKey}</strong>
                              <br />
                              <small className="text-muted">
                                {section.componentKey} • ID: {section.sectionId}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          {section.status ? (
                            <span className="badge bg-success">Visible</span>
                          ) : (
                            <span className="badge bg-secondary">Hidden</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => setEditingSection(section)}
                              title="Edit Content"
                            >
                              <i className="fa fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => moveSectionUp(index)}
                              disabled={index === 0}
                              title="Move Up"
                            >
                              <i className="fa fa-arrow-up"></i>
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => moveSectionDown(index)}
                              disabled={index === sections.length - 1}
                              title="Move Down"
                            >
                              <i className="fa fa-arrow-down"></i>
                            </button>
                            <button
                              className="btn btn-outline-info"
                              onClick={() =>
                                handleToggleSectionStatus(
                                  section.sectionId,
                                  section.status
                                )
                              }
                              title={section.status ? 'Hide' : 'Show'}
                            >
                              <i
                                className={
                                  section.status ? 'fa fa-eye' : 'fa fa-ban'
                                }
                              ></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() =>
                                handleDeleteSection(section.sectionId)
                              }
                              title="Delete"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
            </>
          )}

          {/* SEO Tab - Copy from PageBuilder.jsx */}
          {activeTab === 'seo' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">SEO Settings</h5>
                <button className="btn btn-primary" onClick={handleSavePageDetails}>
                  <i className="fa fa-save me-2"></i>
                  Save SEO
                </button>
              </div>
              <p className="text-muted small mb-4">
                Optimize your page for search engines and social media sharing.
              </p>

              {/* Primary SEO Section */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 className="mb-0">
                    <i className="fa fa-search text-primary me-2"></i>
                    Primary SEO
                  </h6>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Focus Keyword</label>
                    <input
                      type="text"
                      className="form-control"
                      value={seo.focusKeyword}
                      onChange={(e) => setSeo({ ...seo, focusKeyword: e.target.value })}
                      placeholder="Enter primary keyword"
                      maxLength={100}
                    />
                    <small className="text-muted">Main keyword for this page</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Meta Title
                      <span className="badge bg-secondary ms-2">{seo.metaTitle.length}/70</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={seo.metaTitle}
                      onChange={(e) => setSeo({ ...seo, metaTitle: e.target.value })}
                      placeholder="Enter meta title"
                      maxLength={70}
                    />
                    <small className={seo.metaTitle.length > 60 ? 'text-warning' : 'text-muted'}>
                      Optimal length: 50-60 characters
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Meta Description
                      <span className="badge bg-secondary ms-2">{seo.metaDescription.length}/160</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={seo.metaDescription}
                      onChange={(e) => setSeo({ ...seo, metaDescription: e.target.value })}
                      placeholder="Enter meta description"
                      maxLength={160}
                    />
                    <small className={seo.metaDescription.length > 155 ? 'text-warning' : 'text-muted'}>
                      Optimal length: 120-155 characters
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Meta Keywords</label>
                    <input
                      type="text"
                      className="form-control"
                      value={seo.metaKeywords}
                      onChange={(e) => setSeo({ ...seo, metaKeywords: e.target.value })}
                      placeholder="keyword1, keyword2, keyword3"
                    />
                    <small className="text-muted">Comma-separated keywords</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Meta Robots</label>
                    <select
                      className="form-select"
                      value={seo.metaRobots}
                      onChange={(e) => setSeo({ ...seo, metaRobots: e.target.value })}
                    >
                      <option value="index,follow">Index, Follow (Default)</option>
                      <option value="noindex,follow">No Index, Follow</option>
                      <option value="index,nofollow">Index, No Follow</option>
                      <option value="noindex,nofollow">No Index, No Follow</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Open Graph Section */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 className="mb-0">
                    <i className="fab fa-facebook text-primary me-2"></i>
                    Open Graph (Facebook/LinkedIn)
                  </h6>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">OG Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={seo.ogTitle}
                      onChange={(e) => setSeo({ ...seo, ogTitle: e.target.value })}
                      placeholder={seo.metaTitle || "Enter OG title"}
                      maxLength={100}
                    />
                    <small className="text-muted">Leave empty to use Meta Title</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">OG Description</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={seo.ogDescription}
                      onChange={(e) => setSeo({ ...seo, ogDescription: e.target.value })}
                      placeholder={seo.metaDescription || "Enter OG description"}
                      maxLength={200}
                    />
                    <small className="text-muted">Leave empty to use Meta Description</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">OG Type</label>
                    <select
                      className="form-select"
                      value={seo.ogType}
                      onChange={(e) => setSeo({ ...seo, ogType: e.target.value })}
                    >
                      <option value="website">Website</option>
                      <option value="article">Article</option>
                      <option value="product">Product</option>
                      <option value="blog">Blog</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Twitter Card Section */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 className="mb-0">
                    <i className="fab fa-twitter text-primary me-2"></i>
                    Twitter Card
                  </h6>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Card Type</label>
                    <select
                      className="form-select"
                      value={seo.twitterCard}
                      onChange={(e) => setSeo({ ...seo, twitterCard: e.target.value })}
                    >
                      <option value="summary">Summary</option>
                      <option value="summary_large_image">Summary Large Image</option>
                      <option value="app">App</option>
                      <option value="player">Player</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Twitter Site</label>
                    <input
                      type="text"
                      className="form-control"
                      value={seo.twitterSite}
                      onChange={(e) => setSeo({ ...seo, twitterSite: e.target.value })}
                      placeholder="@yoursite"
                    />
                    <small className="text-muted">Your Twitter handle</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Twitter Creator</label>
                    <input
                      type="text"
                      className="form-control"
                      value={seo.twitterCreator}
                      onChange={(e) => setSeo({ ...seo, twitterCreator: e.target.value })}
                      placeholder="@creator"
                    />
                    <small className="text-muted">Content creator&apos;s handle</small>
                  </div>
                </div>
              </div>

              {/* Advanced SEO Section */}
              <div className="card mb-4">
                <div className="card-header">
                  <h6 className="mb-0">
                    <i className="fa fa-cogs text-primary me-2"></i>
                    Advanced SEO
                  </h6>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Canonical URL</label>
                    <input
                      type="text"
                      className="form-control"
                      value={seo.canonicalUrl}
                      onChange={(e) => setSeo({ ...seo, canonicalUrl: e.target.value })}
                      placeholder="https://example.com/page"
                    />
                    <small className="text-muted">Preferred URL for this page</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Schema Type</label>
                    <select
                      className="form-select"
                      value={seo.schemaType}
                      onChange={(e) => setSeo({ ...seo, schemaType: e.target.value })}
                    >
                      <option value="None">None</option>
                      <option value="WebPage">Web Page</option>
                      <option value="Article">Article</option>
                      <option value="Product">Product</option>
                      <option value="Organization">Organization</option>
                      <option value="FAQPage">FAQ Page</option>
                      <option value="BreadcrumbList">Breadcrumb List</option>
                    </select>
                    <small className="text-muted">Structured data type for rich snippets</small>
                  </div>

                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="noindexCheck"
                      checked={seo.noindex}
                      onChange={(e) => setSeo({ ...seo, noindex: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="noindexCheck">
                      <strong>No Index</strong>
                      <div><small className="text-muted">Prevent search engines from indexing</small></div>
                    </label>
                  </div>

                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="nofollowCheck"
                      checked={seo.nofollow}
                      onChange={(e) => setSeo({ ...seo, nofollow: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="nofollowCheck">
                      <strong>No Follow</strong>
                      <div><small className="text-muted">Prevent search engines from following links</small></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Page Details Tab */}
          {activeTab === 'details' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Page Details</h5>
                <button className="btn btn-primary" onClick={handleSavePageDetails}>
                  <i className="fa fa-save me-2"></i>
                  Save Details
                </button>
              </div>
              <p className="text-muted small mb-4">
                Configure page settings and metadata.
              </p>

              <div className="card mb-4">
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Page Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      value={pageName}
                      onChange={(e) => setPageName(e.target.value)}
                      placeholder="Enter page name"
                    />
                    <small className="text-muted">Display name for this page</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Page URL *</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      value={pageUrl}
                      onChange={(e) => setPageUrl(e.target.value)}
                      placeholder="/page-url"
                    />
                    <small className="text-muted">URL path for this page (e.g., /about-us)</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Slug</label>
                    <input
                      type="text"
                      className="form-control"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="page-slug"
                    />
                    <small className="text-muted">URL-friendly identifier (auto-generated from URL)</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Page Type</label>
                    <select className="form-select form-select-lg" value={pageType} onChange={(e) => setPageType(e.target.value)}>
                      <option value="landing">Landing Page</option>
                      <option value="category">Category Page</option>
                      <option value="product">Product Page</option>
                      <option value="simple">Simple Page</option>
                    </select>
                    <small className="text-muted">Type of page for organization</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Status</label>
                    <select className="form-select form-select-lg" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                    <small className="text-muted">Publication status of this page</small>
                  </div>

                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isPrivateCheck"
                      checked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="isPrivateCheck">
                      <strong>Private Page</strong>
                      <div><small className="text-muted">Only accessible to authenticated users</small></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header bg-info text-white">
                  <h6 className="mb-0">
                    <i className="fa fa-info-circle me-2"></i>
                    Page Information
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <strong>Created:</strong> {page?.createdAt ? new Date(page.createdAt).toLocaleString() : 'N/A'}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Updated:</strong> {page?.updatedAt ? new Date(page.updatedAt).toLocaleString() : 'N/A'}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Version:</strong> {page?.version || 1}
                    </div>
                    {page?.publishedAt && (
                      <div className="col-md-6 mb-2">
                        <strong>Published:</strong> {new Date(page.publishedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Component Picker Modal */}
      {showComponentPicker && (
        <ComponentPicker
          onSelect={handleAddSection}
          onClose={() => setShowComponentPicker(false)}
        />
      )}
    </div>
  );
};

export default PageBuilder;
